import type { ServiceItemListQuery } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { CatalogueRowActions } from '../../../../../shared/components/CatalogueRowActions';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { formatInterval } from '../../../policies/interval-labels';
import type { ServiceItemView } from '../service-items.api';

interface BuildServiceItemColumnsArgs {
  /** Class code to name, so a row reads "Tractor head" and not its code. */
  classNames: Map<string, string>;
  list: Pick<ListQueryControls<ServiceItemListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  /** Without it the actions column is left out: `maintenance.view` alone reads the catalogue. */
  actions?: {
    onEdit: (row: ServiceItemView) => void;
    onDeactivate: (row: ServiceItemView) => Promise<void>;
    onReactivate: (row: ServiceItemView) => Promise<void>;
  };
}

/** Each column's `key` is its sort field or filter param in `serviceItemListQuerySchema`. */
export function buildServiceItemColumns({ list, classNames, actions }: BuildServiceItemColumnsArgs): TableColumnsType<ServiceItemView> {
  const columns: TableColumnsType<ServiceItemView> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: true,
      sortOrder: list.sortOrderFor('code'),
      render: (code: string) => <span className="font-mono">{code}</span>,
    },
    { title: 'Name', dataIndex: 'name', key: 'name', sorter: true, sortOrder: list.sortOrderFor('name') },
    { title: 'Default interval', key: 'interval', render: (_, item) => formatInterval(item) },
    {
      title: 'Applies to',
      key: 'truckClasses',
      render: (_, item) =>
        item.truckClassCodes.length === 0 ? (
          <span className="text-muted">No truck class yet</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {item.truckClassCodes.map((code) => (
              <Tag key={code}>{classNames.get(code) ?? code}</Tag>
            ))}
          </div>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'status',
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      filteredValue: list.filteredValueFor('status'),
      render: (isActive: boolean) => <Tag color={isActive ? 'success' : 'default'}>{isActive ? 'Active' : 'Inactive'}</Tag>,
    },
  ];
  if (actions) columns.push({ title: '', key: 'actions', render: (_, item) => <CatalogueRowActions row={item} {...actions} /> });
  return columns;
}
